import { FolderSearch, ServerCrash } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { PageHead } from '@/admin-area/ui/PageHead';
import { StateMessage } from '@/admin-area/ui/StateMessage';
import { Button } from '@/components/ui/button';
import { Skeleton, SkeletonRegion } from '@/components/ui/skeleton';
import type { OverviewItem, OverviewReply } from '../api-types';
import { ConsentPanel } from './ConsentPanel';
import { FindingsPanel } from './FindingsPanel';
import { FolderTree } from './FolderTree';
import { hotspotsOf } from './hotspots';
import { RefreshLine } from './RefreshLine';
import { RuleTitlesContext } from './rule-titles';
import { SummaryStrip } from './SummaryStrip';
import { useHotspots } from './use-hotspots';
import { useOverview } from './use-overview';
import { useRuleTitles } from './use-rule-titles';
import { WorkList } from './WorkList';

/** The root: the tiles, then the work list (what waits) beside the tree (where to go). */
function Root({
  reply,
  drives,
  linkFor,
}: {
  readonly reply: OverviewReply;
  readonly drives: readonly OverviewItem[];
  readonly linkFor: (id: string | undefined) => string;
}) {
  const hot = useHotspots(drives);
  const lists = hot.status === 'ready' ? hot.lists : [];
  const findings = [...drives, ...lists.flatMap((list) => list.items)].flatMap(
    (item) => item.findings,
  );
  return (
    <>
      <SummaryStrip items={drives} findings={findings} />
      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,320px)_minmax(0,1fr)]">
        <FolderTree items={reply.items} path={reply.path} linkFor={linkFor} />
        <WorkList rows={hotspotsOf(lists)} loading={hot.status === 'loading'} linkFor={linkFor} />
      </div>
    </>
  );
}

function Loaded({
  reply,
  linkFor,
  onDue,
}: {
  readonly reply: OverviewReply;
  readonly linkFor: (id: string | undefined) => string;
  readonly onDue: () => void;
}) {
  const { t } = useTranslation('archive');
  const here = reply.path.at(-1);
  const folders = reply.items.filter((item) => item.kind === 'folder');
  let body;
  if (reply.path.length > 0) {
    body = (
      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,320px)_minmax(0,1fr)]">
        <FolderTree items={reply.items} path={reply.path} linkFor={linkFor} />
        <FindingsPanel key={here?.id} items={reply.items} name={here?.name} />
      </div>
    );
  } else if (folders.length === 0) {
    body = (
      <StateMessage
        icon={FolderSearch}
        title={t('overview.noDrives')}
        text={t('overview.noDrivesText')}
      />
    );
  } else {
    body = <Root reply={reply} drives={folders} linkFor={linkFor} />;
  }
  return (
    <>
      <PageHead
        title={t('overview.title')}
        summary={t('overview.summary')}
        actions={<RefreshLine reply={reply} onDue={onDue} />}
      />
      {body}
    </>
  );
}

/**
 * Panoramica degli scostamenti (#788): the drives, then folder by folder, with the findings of the
 * folder in view. The folder is in the address (`?path=`); filters and page size per U6.
 */
export function OverviewPage() {
  const { t } = useTranslation('archive');
  const [params] = useSearchParams();
  const path = params.get('path') ?? undefined;
  const { state, reload, retry } = useOverview(path);
  const titles = useRuleTitles();
  const linkFor = (id: string | undefined) =>
    id === undefined ? '/archive' : `/archive?path=${encodeURIComponent(id)}`;

  if (state.status === 'loading') {
    return (
      <SkeletonRegion label={t('common.loading')} className="flex flex-col gap-3">
        <Skeleton className="h-10 w-1/2" />
        <Skeleton className="h-28 w-full" />
        <Skeleton className="h-48 w-full" />
      </SkeletonRegion>
    );
  }
  if (state.status === 'consent') {
    return <ConsentPanel onGranted={retry} />;
  }
  if (state.status === 'unreachable') {
    return (
      <StateMessage
        role="alert"
        tone="error"
        icon={ServerCrash}
        title={t('overview.unreachable.title')}
        text={t('overview.unreachable.text')}
        action={
          <Button variant="outline" onClick={retry}>
            {t('common.retry')}
          </Button>
        }
      />
    );
  }
  return (
    <RuleTitlesContext.Provider value={titles}>
      <div className="flex flex-col gap-4">
        <Loaded reply={state.reply} linkFor={linkFor} onDue={reload} />
      </div>
    </RuleTitlesContext.Provider>
  );
}
